import React from "react";
import { Tag, Tooltip } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { PosProduct } from "../../../../Types";
import { IMAGE_URL } from "../../../../Constant";

interface ProductCardProps {
  product: PosProduct;
  isWholeSale: boolean;
  compact?: boolean;
  onAdd: (product: PosProduct) => void;
}

export function getProductImage(image_paths: string): string | null {
  try {
    const imgs = JSON.parse(image_paths || "[]");
    if (imgs.length > 0) return `${IMAGE_URL}/${imgs[0]}`;
  } catch {}
  return null;
}

const StockBadge = ({
  totalQty,
  isOutOfStock,
}: {
  totalQty: number;
  isOutOfStock: boolean;
}) => (
  <Tag
    color={isOutOfStock ? "error" : totalQty < 5 ? "warning" : "success"}
    className="m-0 text-[10px] px-1.5 py-0 leading-5 font-medium flex-shrink-0"
  >
    {isOutOfStock ? "OUT" : totalQty}
  </Tag>
);

const Thumbnail = ({
  imgSrc,
  size,
}: {
  imgSrc: string | null;
  size: number;
}) => (
  <div
    className="flex-shrink-0 rounded-md overflow-hidden border border-gray-100 bg-gray-50 flex items-center justify-center"
    style={{ width: size, height: size }}
  >
    {imgSrc ? (
      <img src={imgSrc} alt="" className="w-full h-full object-cover" />
    ) : (
      <span className="text-gray-300 text-[10px] font-medium">IMG</span>
    )}
  </div>
);

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  isWholeSale,
  compact,
  onAdd,
}) => {
  const defaultStock =
    product.stocks.find((s) => s.is_default === 1) || product.stocks[0];
  const price = defaultStock
    ? isWholeSale
      ? defaultStock.wholesale_price
      : defaultStock.retail_price
    : product.mrp;

  const totalQty = product.stocks.reduce((sum, s) => sum + s.quantity, 0);
  const isOutOfStock = totalQty <= 0;
  const imgSrc = getProductImage(product.image_paths);

  const baseRow = `flex items-center gap-2.5 border rounded-lg transition-all duration-100`;
  const stateRow = isOutOfStock
    ? "opacity-45 cursor-not-allowed bg-gray-50 border-gray-100"
    : "bg-white border-gray-200 hover:border-gray-400 hover:bg-gray-50 cursor-pointer";

  if (compact) {
    return (
      <Tooltip title={isOutOfStock ? "Out of stock" : ""} placement="left">
        <div
          role="button"
          onClick={() => !isOutOfStock && onAdd(product)}
          className={`${baseRow} ${stateRow} px-2.5 py-1.5`}
        >
          <Thumbnail imgSrc={imgSrc} size={28} />

          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-gray-400 font-mono leading-none mb-0.5">
              {product.sku_code}
            </p>
            <p className="text-[11px] font-medium text-gray-800 truncate leading-tight">
              {product.title}
            </p>
          </div>

          <span className="text-[11px] font-medium text-gray-900 tabular-nums flex-shrink-0">
            Rs.{price?.toFixed(2)}
          </span>
          <StockBadge totalQty={totalQty} isOutOfStock={isOutOfStock} />
          <PlusOutlined className="text-gray-300 text-xs flex-shrink-0" />
        </div>
      </Tooltip>
    );
  }

  return (
    <div
      role="button"
      onClick={() => !isOutOfStock && onAdd(product)}
      title={product.title}
      className={`flex flex-col gap-2 rounded-lg border p-2 transition-all duration-100
        ${
          isOutOfStock
            ? "opacity-45 cursor-not-allowed bg-gray-50 border-gray-100"
            : "bg-white border-gray-200 hover:border-gray-400 hover:shadow-sm cursor-pointer"
        }`}
    >
      {/* Image + Info row */}
      <div className="flex items-start gap-2">
        <Thumbnail imgSrc={imgSrc} size={36} />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] text-gray-400 font-mono leading-none mb-0.5">
            {product.sku_code}
          </p>
          <p className="text-[11px] font-medium text-gray-800 leading-snug line-clamp-2">
            {product.title}
          </p>
        </div>
      </div>

      {/* Price + Stock */}
      <div className="flex items-center justify-between border-t border-gray-100 pt-1.5">
        <span className="text-xs font-medium text-gray-900 tabular-nums">
          Rs.{price?.toFixed(2)}
        </span>
        <StockBadge totalQty={totalQty} isOutOfStock={isOutOfStock} />
      </div>
    </div>
  );
};
